import React from 'react';
import {
  ArchiveBoxIcon,
  CodeBracketIcon,
  DocumentIcon,
  DocumentTextIcon,
  FilmIcon,
  MusicalNoteIcon,
  PresentationChartBarIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

export const COWORK_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];

export interface CoworkFileTypeMeta {
  label: string;
  bg: string;
  fg: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const FILE_TYPE_META: Record<string, CoworkFileTypeMeta> = {
  md:       { label: 'Markdown', bg: '#DCFCE7', fg: '#15803D', Icon: CodeBracketIcon },
  markdown: { label: 'Markdown', bg: '#DCFCE7', fg: '#15803D', Icon: CodeBracketIcon },
  json:     { label: 'JSON', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  yaml:     { label: 'YAML', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  yml:      { label: 'YAML', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  xml:      { label: 'XML', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  toml:     { label: 'TOML', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  ini:      { label: 'INI', bg: '#FEF3C7', fg: '#A16207', Icon: CodeBracketIcon },
  js:       { label: 'JavaScript', bg: '#FEF9C3', fg: '#854D0E', Icon: CodeBracketIcon },
  mjs:      { label: 'JavaScript', bg: '#FEF9C3', fg: '#854D0E', Icon: CodeBracketIcon },
  cjs:      { label: 'JavaScript', bg: '#FEF9C3', fg: '#854D0E', Icon: CodeBracketIcon },
  ts:       { label: 'TypeScript', bg: '#DBEAFE', fg: '#1E40AF', Icon: CodeBracketIcon },
  tsx:      { label: 'React', bg: '#CFFAFE', fg: '#0E7490', Icon: CodeBracketIcon },
  jsx:      { label: 'React', bg: '#CFFAFE', fg: '#0E7490', Icon: CodeBracketIcon },
  vue:      { label: 'Vue', bg: '#DCFCE7', fg: '#15803D', Icon: CodeBracketIcon },
  py:       { label: 'Python', bg: '#DBEAFE', fg: '#1E40AF', Icon: CodeBracketIcon },
  go:       { label: 'Go', bg: '#CFFAFE', fg: '#0E7490', Icon: CodeBracketIcon },
  java:     { label: 'Java', bg: '#FEE2E2', fg: '#B91C1C', Icon: CodeBracketIcon },
  rb:       { label: 'Ruby', bg: '#FEE2E2', fg: '#B91C1C', Icon: CodeBracketIcon },
  rs:       { label: 'Rust', bg: '#FFEDD5', fg: '#9A3412', Icon: CodeBracketIcon },
  php:      { label: 'PHP', bg: '#EDE9FE', fg: '#6D28D9', Icon: CodeBracketIcon },
  swift:    { label: 'Swift', bg: '#FFEDD5', fg: '#9A3412', Icon: CodeBracketIcon },
  c:        { label: 'C', bg: '#DBEAFE', fg: '#1E40AF', Icon: CodeBracketIcon },
  cpp:      { label: 'C++', bg: '#DBEAFE', fg: '#1E40AF', Icon: CodeBracketIcon },
  cs:       { label: 'C#', bg: '#EDE9FE', fg: '#6D28D9', Icon: CodeBracketIcon },
  sh:       { label: 'Shell', bg: '#E5E7EB', fg: '#374151', Icon: CodeBracketIcon },
  bash:     { label: 'Shell', bg: '#E5E7EB', fg: '#374151', Icon: CodeBracketIcon },
  zsh:      { label: 'Shell', bg: '#E5E7EB', fg: '#374151', Icon: CodeBracketIcon },
  html:     { label: 'HTML', bg: '#FFEDD5', fg: '#9A3412', Icon: CodeBracketIcon },
  css:      { label: 'CSS', bg: '#DBEAFE', fg: '#1E40AF', Icon: CodeBracketIcon },
  scss:     { label: 'SCSS', bg: '#FCE7F3', fg: '#9D174D', Icon: CodeBracketIcon },
  sql:      { label: 'SQL', bg: '#FEE2E2', fg: '#B91C1C', Icon: CodeBracketIcon },
  pdf:      { label: 'PDF', bg: '#FEE2E2', fg: '#B91C1C', Icon: DocumentTextIcon },
  doc:      { label: 'Word', bg: '#DBEAFE', fg: '#1E40AF', Icon: DocumentTextIcon },
  docx:     { label: 'Word', bg: '#DBEAFE', fg: '#1E40AF', Icon: DocumentTextIcon },
  xls:      { label: 'Excel', bg: '#DCFCE7', fg: '#15803D', Icon: TableCellsIcon },
  xlsx:     { label: 'Excel', bg: '#DCFCE7', fg: '#15803D', Icon: TableCellsIcon },
  csv:      { label: 'CSV', bg: '#DCFCE7', fg: '#15803D', Icon: TableCellsIcon },
  ppt:      { label: 'PowerPoint', bg: '#FFEDD5', fg: '#9A3412', Icon: PresentationChartBarIcon },
  pptx:     { label: 'PowerPoint', bg: '#FFEDD5', fg: '#9A3412', Icon: PresentationChartBarIcon },
  txt:      { label: 'Text', bg: '#F3F4F6', fg: '#374151', Icon: DocumentTextIcon },
  log:      { label: 'Log', bg: '#F3F4F6', fg: '#374151', Icon: DocumentTextIcon },
  zip:      { label: 'Archive', bg: '#E5E7EB', fg: '#374151', Icon: ArchiveBoxIcon },
  rar:      { label: 'Archive', bg: '#E5E7EB', fg: '#374151', Icon: ArchiveBoxIcon },
  tar:      { label: 'Archive', bg: '#E5E7EB', fg: '#374151', Icon: ArchiveBoxIcon },
  gz:       { label: 'Archive', bg: '#E5E7EB', fg: '#374151', Icon: ArchiveBoxIcon },
  '7z':     { label: 'Archive', bg: '#E5E7EB', fg: '#374151', Icon: ArchiveBoxIcon },
  mp3:      { label: 'Audio', bg: '#EDE9FE', fg: '#6D28D9', Icon: MusicalNoteIcon },
  wav:      { label: 'Audio', bg: '#EDE9FE', fg: '#6D28D9', Icon: MusicalNoteIcon },
  flac:     { label: 'Audio', bg: '#EDE9FE', fg: '#6D28D9', Icon: MusicalNoteIcon },
  mp4:      { label: 'Video', bg: '#FCE7F3', fg: '#9D174D', Icon: FilmIcon },
  mov:      { label: 'Video', bg: '#FCE7F3', fg: '#9D174D', Icon: FilmIcon },
  avi:      { label: 'Video', bg: '#FCE7F3', fg: '#9D174D', Icon: FilmIcon },
  mkv:      { label: 'Video', bg: '#FCE7F3', fg: '#9D174D', Icon: FilmIcon },
};

const DEFAULT_FILE_META: CoworkFileTypeMeta = {
  label: 'File',
  bg: '#F3F4F6',
  fg: '#374151',
  Icon: DocumentIcon,
};

const IMAGE_META: CoworkFileTypeMeta = {
  label: 'Image',
  bg: '#FCE7F3',
  fg: '#9D174D',
  Icon: DocumentIcon,
};

/** 判断本地路径是否为支持预览的图片。 */
export function isCoworkImagePath(path: string): boolean {
  const normalized = path.toLowerCase().split(/[?#]/, 1)[0];
  return COWORK_IMAGE_EXTENSIONS.some((extension) => normalized.endsWith(extension));
}

/** 返回文件名；同时兼容 macOS/Linux 与 Windows 路径。 */
export function getCoworkFileName(path: string): string {
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || path;
}

/** 返回 Cowork 附件卡片使用的文件类型、图标与配色。 */
export function getCoworkFileMeta(path: string): CoworkFileTypeMeta {
  if (isCoworkImagePath(path)) return IMAGE_META;
  const name = getCoworkFileName(path);
  const extensionIndex = name.lastIndexOf('.');
  const extension = extensionIndex >= 0 ? name.slice(extensionIndex + 1).toLowerCase() : '';
  return FILE_TYPE_META[extension] || {
    ...DEFAULT_FILE_META,
    label: extension ? extension.toUpperCase() : DEFAULT_FILE_META.label,
  };
}

/** 将字节数格式化为附件卡片的紧凑文件大小。 */
export function formatCoworkFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
}
